/**
 * Mock Backend Service for Development/Demo Mode
 *
 * Provides mock responses for all API endpoints so the SDK can run
 * without a real backend connection. Used when api_key is 'demo' or
 * when explicitly configured.
 */

import type { VerificationMethod } from '../orchestration';
import { redactSecrets, maskSecret } from '../utils/log-redaction';

// ============================================================
// Types
// ============================================================

export interface MockRequirementsData {
  /** Session ID for this verification flow */
  session_id: string;
  methods: VerificationMethod[];
  regime: string;
  country: string;
  country_name: string;
  rule_name: string;
  min_age?: number;
  liveness_action_count?: number;
}

export interface MockRequirementsResponse {
  code: number;
  success: boolean;
  data: MockRequirementsData;
}

export interface MockLivenessStep {
  action: string;
  params: {
    window?: number;
    threshold?: number;
    angle?: number;
    hold?: number;
  };
}

export interface MockLivenessChallenge {
  challenge_id: string;
  steps: MockLivenessStep[];
  expires_at: number;
}

export interface MockLivenessVerifyResponse {
  data: {
    accept: boolean;
    confidence: number;
    reason?: string;
  };
}

export interface MockOcrResponse {
  task_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
}

export interface MockOcrStatusResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: {
    document_type: string;
    country: string;
    date_of_birth?: string;
    age?: number;
    is_valid: boolean;
  };
  error?: string;
  progress?: number;
}

// ============================================================
// Mock Response Data
// ============================================================

/**
 * Generate a mock UUID v7 for session IDs
 */
function generateMockSessionId(): string {
  const timestamp = Date.now().toString(16).padStart(12, '0');
  const random = Math.random().toString(16).slice(2, 14).padStart(12, '0');
  return `${timestamp.slice(0, 8)}-${timestamp.slice(8, 12)}-7${random.slice(0, 3)}-${random.slice(3, 7)}-${random.slice(7)}demo`;
}

export const MOCK_REQUIREMENTS: MockRequirementsData = {
  session_id: generateMockSessionId(),
  methods: ['liveness', 'age'],
  regime: 'standard',
  country: 'US',
  country_name: 'United States',
  rule_name: 'default',
  min_age: 18,
  liveness_action_count: 1,
};

export const MOCK_REQUIREMENTS_WITH_OCR: MockRequirementsData = {
  session_id: generateMockSessionId(),
  methods: ['liveness', 'age', 'ocr'],
  regime: 'strict',
  country: 'DE',
  country_name: 'Germany',
  rule_name: 'eu-strict',
  min_age: 18,
  liveness_action_count: 1,
};

export const MOCK_LIVENESS_CHALLENGE: MockLivenessChallenge = {
  challenge_id: 'mock-challenge-' + Date.now(),
  steps: [
    // 10 second window, lower threshold (0.18) for easier blink detection
    { action: 'BLINK', params: { window: 10000, threshold: 0.18 } },
    { action: 'SMILE', params: { window: 10000, threshold: 0.15 } },
    { action: 'LOOK_LEFT', params: { window: 10000, angle: 6, hold: 300 } },
  ],
  expires_at: Date.now() + 300000, // 5 minutes from now
};

export const MOCK_LIVENESS_VERIFY_SUCCESS: MockLivenessVerifyResponse = {
  data: {
    accept: true,
    confidence: 0.95,
  },
};

export const MOCK_LIVENESS_VERIFY_FAILURE: MockLivenessVerifyResponse = {
  data: {
    accept: false,
    confidence: 0.3,
    reason: 'Too many actions failed',
  },
};

// ============================================================
// Mock Backend Class
// ============================================================

export interface MockBackendConfig {
  /** Delay in ms for mock API responses (default: 500) */
  delay?: number;
  /** Enable random failures for testing (default: false) */
  randomFailures?: boolean;
  /** Failure rate 0-1 (default: 0.1) */
  failureRate?: number;
  /** Include OCR in requirements (default: false) */
  includeOcr?: boolean;
  /** Debug logging (default: false) */
  debug?: boolean;
}

export class MockBackend {
  private config: Required<MockBackendConfig>;
  private ocrJobStatus: Map<string, MockOcrStatusResponse> = new Map();

  constructor(config: MockBackendConfig = {}) {
    this.config = {
      delay: 500,
      randomFailures: false,
      failureRate: 0.1,
      includeOcr: false,
      debug: false,
      ...config,
    };
  }

  private log(message: string, ...args: unknown[]): void {
    if (this.config.debug) {
      // Redact sensitive data (API keys, tokens, etc.) from log output
      const safeArgs = args.map((arg) => {
        if (typeof arg === 'string' && message.toLowerCase().includes('apikey')) {
          return maskSecret(arg);
        }
        if (typeof arg === 'object' && arg !== null) {
          return redactSecrets(arg as Record<string, unknown>);
        }
        return arg;
      });
      console.log(`[MockBackend] ${message}`, ...safeArgs);
    }
  }

  private async delay(): Promise<void> {
    const ms = this.config.delay + Math.random() * 100; // Add jitter
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  private shouldFail(): boolean {
    return this.config.randomFailures && Math.random() < this.config.failureRate;
  }

  /**
   * Mock /v1/requirements/check
   */
  async checkRequirements(apiKey: string): Promise<MockRequirementsResponse> {
    this.log('checkRequirements called with apiKey:', apiKey);
    await this.delay();

    if (this.shouldFail()) {
      throw new Error('Mock: Requirements check failed');
    }

    const data = this.config.includeOcr ? MOCK_REQUIREMENTS_WITH_OCR : MOCK_REQUIREMENTS;

    return {
      code: 200,
      success: true,
      data: {
        ...data,
        // Generate a new session_id for each call
        session_id: generateMockSessionId(),
      },
    };
  }

  /**
   * Mock GET /v1/liveness/challenge
   */
  async getLivenessChallenge(): Promise<{ data: MockLivenessChallenge }> {
    this.log('getLivenessChallenge called');
    await this.delay();

    if (this.shouldFail()) {
      throw new Error('Mock: Failed to get liveness challenge');
    }

    return {
      data: {
        ...MOCK_LIVENESS_CHALLENGE,
        challenge_id: 'mock-challenge-' + Date.now(),
        expires_at: Date.now() + 300000,
      },
    };
  }

  /**
   * Mock POST /v1/liveness/verify
   */
  async verifyLiveness(
    challengeId: string,
    results: Array<{ action: string; ok: boolean; score: number }>,
    durationMs: number
  ): Promise<MockLivenessVerifyResponse> {
    this.log('verifyLiveness called:', { challengeId, results, durationMs });
    await this.delay();

    if (this.shouldFail()) {
      return {
        data: {
          accept: false,
          confidence: 0.3,
          reason: 'Mock: Random verification failure',
        },
      };
    }

    // Calculate score based on results
    const passedActions = results.filter((r) => r.ok).length;
    const totalActions = results.length;
    const score = totalActions > 0 ? passedActions / totalActions : 0;

    // Calculate weighted confidence from individual scores
    const avgScore = results.reduce((sum, r) => sum + r.score, 0) / (totalActions || 1);
    const confidence = Math.min(score, avgScore);

    return {
      data: {
        accept: score >= 0.6,
        confidence,
        reason: score < 0.6 ? 'Too many actions failed' : undefined,
      },
    };
  }

  /**
   * Mock POST /api/v1/ocr (document upload)
   */
  async uploadDocument(
    documentType: string,
    side: 'front' | 'back',
    imageData: string
  ): Promise<MockOcrResponse> {
    this.log('uploadDocument called:', { documentType, side, imageDataLength: imageData.length });
    await this.delay();

    if (this.shouldFail()) {
      throw new Error('Mock: Document upload failed');
    }

    const taskId = 'mock-ocr-' + Date.now() + '-' + side;

    // Simulate async processing - start as pending
    this.ocrJobStatus.set(taskId, {
      status: 'pending',
      progress: 0,
    });

    // Simulate processing progress
    this.simulateOcrProcessing(taskId);

    return {
      task_id: taskId,
      status: 'pending',
    };
  }

  /**
   * Simulate OCR processing in background
   */
  private simulateOcrProcessing(taskId: string): void {
    const progressIntervals = [
      { delay: 500, status: 'processing' as const, progress: 25 },
      { delay: 1000, status: 'processing' as const, progress: 50 },
      { delay: 1500, status: 'processing' as const, progress: 75 },
      { delay: 2000, status: 'completed' as const, progress: 100 },
    ];

    progressIntervals.forEach(({ delay, status, progress }) => {
      setTimeout(() => {
        const current = this.ocrJobStatus.get(taskId);
        if (current && current.status !== 'failed') {
          if (status === 'completed') {
            this.ocrJobStatus.set(taskId, {
              status: 'completed',
              progress: 100,
              result: {
                document_type: 'id_card',
                country: 'US',
                date_of_birth: '1995-06-15',
                age: 29,
                is_valid: true,
              },
            });
          } else {
            this.ocrJobStatus.set(taskId, { status, progress });
          }
        }
      }, delay);
    });
  }

  /**
   * Mock GET /api/v1/ocr/status/{task_id}
   */
  async getOcrStatus(taskId: string): Promise<MockOcrStatusResponse> {
    this.log('getOcrStatus called:', taskId);
    await this.delay();

    if (this.shouldFail()) {
      return {
        status: 'failed',
        error: 'Mock: OCR processing failed',
      };
    }

    const status = this.ocrJobStatus.get(taskId);
    if (!status) {
      return {
        status: 'failed',
        error: 'Task not found',
      };
    }

    return status;
  }

  /**
   * Mock age estimation (client-side, no API call)
   */
  estimateAge(): { age: number; confidence: number; isAdult: boolean } {
    this.log('estimateAge called (mock)');
    // Return a random age for demo purposes
    const age = Math.floor(Math.random() * 30) + 18; // 18-47
    return {
      age,
      confidence: 0.85 + Math.random() * 0.1, // 0.85-0.95
      isAdult: age >= 18,
    };
  }

  /**
   * Generate a mock verification token
   */
  generateVerificationToken(): string {
    return 'mock-token-' + Date.now() + '-' + Math.random().toString(36).substring(2, 10);
  }

  /**
   * Reset state (useful for testing)
   */
  reset(): void {
    this.ocrJobStatus.clear();
  }
}

// ============================================================
// Singleton Export
// ============================================================

let mockBackendInstance: MockBackend | null = null;

export function getMockBackend(config?: MockBackendConfig): MockBackend {
  if (!mockBackendInstance || config) {
    mockBackendInstance = new MockBackend(config);
  }
  return mockBackendInstance;
}

export function resetMockBackend(): void {
  mockBackendInstance = null;
}
